import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../axiosConfig";
import RedWorkerTimeModal from "./RedWorkerTimeModal";

const isActiveNotification = note => {
  const activeStatuses = new Set(["PUBLISHED", "HOME_APPROVAL", "SELECTION", "CONFIRMED", "REPORTING", "STARTED"]);
  const closedWorkerStatuses = new Set(["HOME_DECLINED", "RELEASED", "CANCELLED"]);
  const expiryStatuses = new Set(["PUBLISHED", "HOME_APPROVAL", "SELECTION"]);
  const slotStatus = String(note.Status || "").toUpperCase();
  const workerStatus = String(note.interest_status || "").toUpperCase();
  const scheduledEnd = note.ScheduledEnd ? new Date(note.ScheduledEnd).getTime() : null;
  const expired = expiryStatuses.has(slotStatus) && Number.isFinite(scheduledEnd) && scheduledEnd < Date.now();
  return activeStatuses.has(slotStatus) && !closedWorkerStatuses.has(workerStatus) && !expired;
};

const statusLabel = status => ({
  OPEN: "Kaam ke liye haan bolein",
  INTERESTED: "Jawab bhej diya",
  HOME_APPROVED: "Supervisor ne haan ki",
  HOME_DECLINED: "Supervisor ne mana kiya",
  SELECTED: "Aap select ho gaye",
  STANDBY: "Backup worker",
  RELEASED: "Duty se hata diya",
  PUBLISHED: "Worker chahiye",
  HOME_APPROVAL: "Supervisor ka jawab baki",
  SELECTION: "Worker selection baki",
  CONFIRMED: "Kaam pakka",
  REPORTING: "Pahunch confirm karein",
  STARTED: "Kaam chal raha hai",
  QUALITY_PENDING: "Kaam pura, checking baki",
  APPROVED: "Kaam pura",
  CANCELLED: "Kaam radd",
}[String(status || "").toUpperCase()] || String(status || "-").replaceAll("_", " "));

const isApplicationFull = note => {
  const used = Number(note.ActiveApplicationCount);
  const capacity = Number(note.ApplicationCapacity);
  return Number.isFinite(used) && Number.isFinite(capacity) && capacity > 0 && used >= capacity;
};

const instruction = note => {
  if (note.Title === "RED Time Update Required") return note.Message || "Supervisor ne naya available time select karne ko kaha hai.";
  const status = String(note.interest_status || note.Status || "OPEN").toUpperCase();
  if (!note.interest_status && ["PUBLISHED", "HOME_APPROVAL"].includes(String(note.Status || "").toUpperCase())) {
    if (isApplicationFull(note)) return "Is lot ke liye worker capacity full hai. Supervisor ke decision ka wait karein.";
    return "Kaam karna hai to neeche 'Mujhe Kaam Karna Hai' dabayein.";
  }
  if (status === "INTERESTED") return "Aapne haan bol diya hai. Supervisor ke jawab ka wait karein.";
  if (status === "HOME_APPROVED") return "Supervisor ne haan kar di hai. Final selection ka wait karein.";
  if (status === "SELECTED") return "Aap select ho gaye hain. Diye gaye time par report karein.";
  if (status === "STANDBY") return "Aap backup worker hain. Supervisor ke bulane ka wait karein.";
  if (status === "STARTED") return "Kaam shuru ho gaya hai.";
  if (["APPROVED", "QUALITY_PENDING"].includes(status)) return "Yeh kaam pura ho gaya hai.";
  if (["HOME_DECLINED", "RELEASED", "CANCELLED"].includes(status)) return "Is kaam par ab koi action nahi lena hai.";
  return note.Message || "Kaam ki nayi jankari.";
};

const formatDate = value => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : new Intl.DateTimeFormat("en-IN",{timeZone:"Asia/Kolkata",day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit",hour12:true}).format(date).replace(/\b(am|pm)\b/gi,value=>value.toUpperCase());
};

const redVideoUrl = value => {
  const path=String(value||"").trim();
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base=String(process.env.REACT_APP_API_BASE_PATH||axiosInstance.defaults.baseURLPath||axiosInstance.defaults.baseURL||window.location.origin).replace(/\/+$/,"");
  return `${base}${path.startsWith("/")?"":"/"}${path}`;
};

export default function RedWorkerNotificationsBell({ employeeId }) {
  const [notifications, setNotifications] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [view, setView] = useState("active");
  const [search, setSearch] = useState("");
  const [timeModal, setTimeModal] = useState(null);

  const loadNotifications = useCallback(async () => {
    if (!employeeId) return;
    try {
      const response = await axiosInstance.get(`/api/red-support-slots/worker/${employeeId}`);
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error("RED worker notification fetch failed:", error);
    }
  }, [employeeId]);

  useEffect(() => {
    loadNotifications();
    const timer = setInterval(loadNotifications, 30000);
    return () => clearInterval(timer);
  }, [loadNotifications]);

  const latestNotifications = useMemo(() => {
    const latestBySlot = new Map();
    notifications.forEach(note => {
      const key = String(note.Id || note.SlotId || note.notification_id);
      if (!latestBySlot.has(key)) latestBySlot.set(key, note);
    });
    return [...latestBySlot.values()];
  }, [notifications]);

  const activeNotifications = useMemo(
    () => latestNotifications.filter(isActiveNotification),
    [latestNotifications]
  );
  const activeIds = useMemo(
    () => new Set(activeNotifications.map(note => note.notification_id)),
    [activeNotifications]
  );
  const historyNotifications = useMemo(
    () => latestNotifications.filter(note => !activeIds.has(note.notification_id)),
    [activeIds, latestNotifications]
  );
  const unreadCount = notifications.filter(note => isActiveNotification(note) && !note.IsRead).length;
  const currentNotifications = view === "active" ? activeNotifications : historyNotifications;
  const visibleNotifications = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return currentNotifications;
    return currentNotifications.filter(note => [note.DisplayLotId, note.LotId, note.Department, note.ProcessName, note.Message]
      .some(value => String(value || "").toLowerCase().includes(query)));
  }, [currentNotifications, search]);

  const openNotifications = async () => {
    setView(activeNotifications.length ? "active" : historyNotifications.length ? "history" : "active");
    setSearch("");
    setModalOpen(true);
    const unreadIds = notifications.filter(note => isActiveNotification(note) && !note.IsRead).map(note => note.notification_id);
    if (!unreadIds.length) return;
    setNotifications(current => current.map(note => unreadIds.includes(note.notification_id) ? { ...note, IsRead: true } : note));
    await Promise.allSettled(unreadIds.map(id => axiosInstance.patch(`/api/red-support-slots/notifications/${id}/read`)));
  };

  const registerInterest = note => setTimeModal({ slot: note, mode: "interest" });
  const changeAvailability = note => setTimeModal({ slot: note, mode: "update" });

  const handleTimeSaved = async ({ selectedSlot, selectedStart, mode }) => {
    if (mode === "update") {
      setNotifications(current => current.map(item => String(item.Id) === String(timeModal?.slot?.Id) ? {
        ...item,
        PreferredScheduledStart: selectedStart,
        PreferredTimeSlots: [{ start: selectedStart, end: selectedSlot?.end }],
      } : item));
    }
    await loadNotifications();
  };

  return <>
    <button type="button" className="btn border rounded-circle position-relative d-flex align-items-center justify-content-center" style={{ width: "44px", height: "44px", backgroundColor: "#eff6ff", borderColor: "#bfdbfe", boxShadow: "0 2px 8px rgba(37, 99, 235, 0.16)" }} onClick={openNotifications} aria-label={`Open notifications${unreadCount ? `, ${unreadCount} unread` : ""}`}>
      <i className="bi bi-bell-fill fs-5" style={{ color: "#2563eb" }} aria-hidden="true" />
      {unreadCount > 0 && <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-2 border-white" style={{ minWidth: 22 }}>{unreadCount > 99 ? "99+" : unreadCount}</span>}
    </button>

    {modalOpen && <div className="modal d-block" role="dialog" aria-modal="true" aria-labelledby="cuttingNotificationsTitle" style={{ backgroundColor: "rgba(15, 23, 42, 0.62)", zIndex: 2000 }}>
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable mx-auto my-2 my-sm-4" style={{ width: "calc(100% - 16px)", maxWidth: "1180px", minHeight: 0 }}>
        <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden" style={{ maxHeight: "calc(100vh - 16px)" }}>
          <div className="modal-header bg-primary text-white px-3 px-sm-4 py-3">
            <div><h5 className="modal-title mb-1" id="cuttingNotificationsTitle"><i className="bi bi-bell-fill me-2" />Kaam Ki Suchna</h5><div className="small opacity-75">Naya lot aur aapke kaam ki jankari</div></div>
            <button type="button" className="btn-close btn-close-white" aria-label="Close" onClick={() => setModalOpen(false)} />
          </div>
          <div className="modal-body bg-light p-2 p-sm-4">
            <div className="row g-2 mb-3">
              <div className="col-6"><button type="button" className={`w-100 rounded-3 border p-2 text-start ${view === "active" ? "bg-danger text-white border-danger" : "bg-white"}`} onClick={() => { setView("active"); setSearch(""); }}><strong>Abhi Ka Kaam</strong><span className={`badge rounded-pill float-end ${view === "active" ? "bg-white text-danger" : "bg-danger"}`}>{activeNotifications.length}</span></button></div>
              <div className="col-6"><button type="button" className={`w-100 rounded-3 border p-2 text-start ${view === "history" ? "bg-secondary text-white border-secondary" : "bg-white"}`} onClick={() => { setView("history"); setSearch(""); }}><strong>Purana Kaam</strong><span className={`badge rounded-pill float-end ${view === "history" ? "bg-white text-secondary" : "bg-secondary"}`}>{historyNotifications.length}</span></button></div>
            </div>
            <div className="input-group mb-3 shadow-sm"><span className="input-group-text bg-white border-end-0"><i className="bi bi-search text-muted" /></span><input type="search" className="form-control border-start-0" placeholder="Lot number ya department khojein..." value={search} onChange={event => setSearch(event.target.value)} /></div>
            {visibleNotifications.length ? visibleNotifications.map(note => {
              const workerStatus = note.interest_status || (note.Status === "PUBLISHED" ? "OPEN" : note.Status) || "OPEN";
              return <article className="rounded-4 p-3 mb-3 bg-white shadow-sm border border-start border-4 border-danger" key={note.notification_id}>
                <div className="d-flex flex-wrap justify-content-between gap-2 mb-3"><div><h5 className="mb-1">Lot {note.DisplayLotId || note.LotId || "Not available"}</h5><div className="small text-muted">{formatDate(note.CreatedAt)}</div></div><span className="badge rounded-pill bg-warning text-dark align-self-start px-3 py-2">{statusLabel(workerStatus)}</span></div>
                <div className="row g-2 mb-3"><div className="col-12 col-sm-4"><div className="bg-light rounded-3 p-2 h-100"><small className="text-muted d-block">Department</small><strong>{note.Department || "RED Process"}</strong></div></div><div className="col-12 col-sm-5"><div className="bg-light rounded-3 p-2 h-100"><small className="text-muted d-block">Work Time</small>{!['SELECTED','STANDBY'].includes(String(note.interest_status||'').toUpperCase())?<><strong className="d-block">{(note.PreferredTimeSlots||[]).length?(note.PreferredTimeSlots||[]).map(item=><span className="d-block" key={item.start}>• {formatDate(item.start)}</span>):"Interested par ek available time select karein"}</strong><span className="small text-muted">Crew selection par aapka allocated time confirm hoga.</span></>:<><strong className="d-block">Start: {formatDate(note.PreferredScheduledStart||note.ScheduledStart)||"Time nahi diya"}</strong><span className="small text-muted">End: {formatDate((note.PreferredTimeSlots||[]).find(item=>new Date(item.start).getTime()===new Date(note.PreferredScheduledStart).getTime())?.end||note.ScheduledEnd)||"Time nahi diya"}</span></>}</div></div><div className="col-12 col-sm-3"><div className="bg-light rounded-3 p-2 h-100"><small className="text-muted d-block">Target Qty</small><strong>{Number(note.TargetQuantity||0).toLocaleString("en-IN")}</strong></div></div></div>
                <div className="rounded-3 border border-warning bg-warning-subtle p-3"><strong><i className="bi bi-info-circle-fill me-2" />{instruction(note)}</strong></div>
                {note.VideoUrl && <div className="rounded-3 border bg-dark p-2 mt-3"><div className="d-flex justify-content-between align-items-center gap-2 mb-2"><span className="small text-white-50"><i className="bi bi-play-circle me-1" />Process Video</span><a className="btn btn-sm btn-outline-light" href={redVideoUrl(note.VideoUrl)} target="_blank" rel="noreferrer">Open Video</a></div><video className="w-100 rounded" style={{maxHeight:320}} src={redVideoUrl(note.VideoUrl)} controls preload="metadata">Your browser does not support video playback.</video></div>}
                {!note.interest_status && ["PUBLISHED", "HOME_APPROVAL"].includes(note.Status) && !isApplicationFull(note) && <div className="d-grid d-sm-flex justify-content-sm-end mt-3"><button type="button" className="btn btn-danger px-4 fw-bold" onClick={() => registerInterest(note)}>Mujhe Kaam Karna Hai</button></div>}
                {["INTERESTED","HOME_APPROVED"].includes(String(note.interest_status||"").toUpperCase()) && ["PUBLISHED","HOME_APPROVAL","SELECTION"].includes(String(note.Status||"").toUpperCase()) && <div className="d-grid d-sm-flex justify-content-sm-end mt-3"><button type="button" className="btn btn-outline-primary px-4" onClick={()=>changeAvailability(note)}><i className="bi bi-clock-history me-2" />Change Available Time</button></div>}
              </article>;
            }) : <div className="text-center text-muted bg-white border rounded-4 p-5"><i className="bi bi-bell-slash fs-1 d-block mb-2" /><strong>Koi notification nahi hai</strong></div>}
          </div>
        </div>
      </div>
    </div>}
    {timeModal && <RedWorkerTimeModal
      employeeId={employeeId}
      mode={timeModal.mode}
      slot={timeModal.slot}
      onClose={() => setTimeModal(null)}
      onSaved={handleTimeSaved}
    />}
  </>;
}
